import {observer} from 'mobx-react-lite'
import React, {useEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Time, Util} from 'yougile-util'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {Icon} from '@components/common/base/icon'
import {useGlobalKeyDown, useGlobalKeyUp} from '@containers/hooks'
import {SearchPopup} from './search-popup'
import {
  SearchPopupContext,
  SearchPopupStore,
  useSearchPopupStore
} from './search-popup-store'
import {
  PredefinedFilter,
  SearchContext,
  SearchStore,
  useSearchStore
} from './search-store'

const TwInput = tw.input`
  cursor-text
  flex-1
  rounded-8
  pl-36
  pr-12
  h-32

  text-sm-regular
  text-panel-text-primary
  placeholder-panel-text-placeholder

  bg-panel-background-constant
  focus:bg-panel-background-constant

  outline-none
  outline-1
  outline-offset-0
  focus:outline
  focus:outline-action-default
  active:outline
  active:outline-1
  active:outline-action-default

  border-solid
  border-panel-line-base
  border-1
  focus:border-action-default
  active:border-action-default
`

const TwIcon = tw(Icon)`
  absolute
  h-full
  left-12
  z-20
`

const TwSearchTop = tw.div`
  relative
`

const useFocusKeyboardHandlers = (store: SearchPopupStore): void => {
  const shiftTsRef = useRef<number>()
  const shiftPressDownRef = useRef<number>(0)
  // If not a Shift key is pressed inbetween two Shift presses,
  // consider double Shift wasn't a user's intention.
  useGlobalKeyDown({
    anyKey(opts) {
      if (opts.key !== 'Shift' && shiftTsRef.current) {
        shiftTsRef.current = undefined
      }

      if (opts.key === 'Shift') {
        shiftPressDownRef.current = Time.now()
      }

      if (opts.key === 'e' && (opts.meta || opts.ctrl)) {
        // Ctrl+E or Cmd+E
        return store.tryOpenPopup()
      } else if (opts.key === 'Escape') {
        // Esc
        return store.tryClosePopup()
      }
      return false
    }
  })

  // Use keyup instead of keydown because Win sends multiple keydowns on button press
  // which creates problems w/ a Shift keystroke
  useGlobalKeyUp({
    anyKey(opts) {
      if (opts.key === 'Shift') {
        const diff = Time.now() - shiftPressDownRef.current
        if (diff > 500) {
          shiftTsRef.current = undefined
          shiftPressDownRef.current = 0
          return false
        }
      }

      // Double press on Shift key; if not a Shift key is pressed inbetween,
      // consider double Shift wasn't a user's intention.
      if (opts.key === 'Shift' && !shiftTsRef.current) {
        shiftTsRef.current = Time.now()
      } else if (opts.key !== 'Shift' && shiftTsRef.current) {
        shiftTsRef.current = undefined
      } else if (opts.key === 'Shift' && shiftTsRef.current) {
        const prev = shiftTsRef.current
        // For a case of a single Shift tap followed by a quick double tap
        shiftTsRef.current = Time.now()
        if (Time.now() - prev < 500) {
          return store.tryOpenPopup()
        }
      }
      return false
    }
  })
}

export interface SearchTopProps {
  defaultPredefinedFilter: PredefinedFilter
  availablePredefinedFilters: PredefinedFilter[]
}

export const SearchTop = ({
  defaultPredefinedFilter,
  availablePredefinedFilters
}: SearchTopProps) => {
  const [searchStore] = useState(
    () =>
      new SearchStore(defaultPredefinedFilter, availablePredefinedFilters, 10)
  )
  const [searchPopupStore] = useState(() => new SearchPopupStore(searchStore))

  useEffect(() => {
    searchStore.sync(defaultPredefinedFilter, availablePredefinedFilters)
  }, [searchStore, defaultPredefinedFilter, availablePredefinedFilters])
  useEffect(searchStore.init, [searchStore])
  useFocusKeyboardHandlers(searchPopupStore)

  return (
    <SearchContext.Provider value={searchStore}>
      <SearchPopupContext.Provider value={searchPopupStore}>
        <SearchTopBase />
      </SearchPopupContext.Provider>
    </SearchContext.Provider>
  )
}

export const SearchTopBase = observer(() => {
  const [state, store] = useSearchStore()
  const [popupState, popupStore] = useSearchPopupStore()
  const refInput = useRef<HTMLInputElement>()

  const bem = Util.bemFrom('search-top')
  const placeholder = SharedBoardsClient.getId()
    ? Lang.msg.share.searchBoard
    : Lang.msg.searchCompany

  useEffect(() => {
    const input = refInput.current
    if (popupState.opened && input) {
      if (input.value) {
        setTimeout(() => {
          input.select()
        }, 0)
      }
    }
  }, [popupState.opened, refInput.current])

  return (
    <>
      <TwSearchTop ref={popupStore.setPopupReferenceEl} className={bem()}>
        <TwIcon icon="IconSearch" color="panel-text-secondary" />
        <TwInput
          ref={refInput}
          placeholder={placeholder}
          onFocus={popupStore.onFakeInputFocus}
          value={state.value}
          onChange={e => store.setValue(e.target.value)}
        />
      </TwSearchTop>
      {popupState.opened && <SearchPopup />}
    </>
  )
})
